import { Link } from 'react-router-dom'
import { Button } from '@gjl/ui'
import { SEO } from '@/components/SEO'
import { GlassCard } from '@/components/ui/glass-card'
import { CaseStudies } from '@/components/CaseStudies'
import { useLuxeHero, useDirectionalReveal, useLuxeGrid } from '@/hooks'
import { ArrowRight, Bot, ShieldCheck, GitBranch, Target, Layers } from 'lucide-react'

const values = [
  {
    icon: ShieldCheck,
    title: 'Guardrails before output',
    description:
      'An LLM that can ship is also one that can hallucinate. I gate every model output against evidence and fail closed — never send an unsupported claim.',
  },
  {
    icon: GitBranch,
    title: 'One source of truth',
    description:
      'Domain rules drift when they live in the prompt, the docs, and the code at once. I single-source contracts so the agent, the tooling, and the docs stay in sync.',
  },
  {
    icon: Target,
    title: 'Operate, don’t override',
    description:
      'The best agents propose and let a human commit. Large tool surfaces with a review step beat full autonomy that nobody can trust.',
  },
  {
    icon: Layers,
    title: 'Ship it to production',
    description:
      'A demo proves nothing. I run these systems unattended on real infrastructure — Bedrock, Lambda, DynamoDB, Terraform, CI/CD — and measure what they do.',
  },
]

export function About() {
  const heroRef = useLuxeHero<HTMLElement>(0.3)

  const introRef = useDirectionalReveal<HTMLElement>('.reveal', 'left', {
    stagger: 0.15,
    blur: 6,
  })

  const valuesRef = useLuxeGrid<HTMLElement>('.value-card', {
    stagger: 0.1,
  })

  const ctaRef = useDirectionalReveal<HTMLElement>('.reveal', 'fade', {
    scale: 0.95,
  })

  return (
    <>
      <SEO
        title="About"
        description="AI systems and agent engineer. I build production AI — multi-agent fleets, grounded LLM tooling, and automated evaluation engines on AWS Bedrock."
        url="https://software.garrettjohnlaw.com/about"
      />

      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden px-4 py-20 md:py-28">
        <div className="bg-gradient-radial-cyber absolute inset-0 opacity-30" />
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <p className="text-primary mb-4 font-mono text-lg font-medium tracking-wide">&lt;about /&gt;</p>
          <h1 className="text-fluid-h1 text-foreground mb-6">About Garrett</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            AI Systems &amp; Agent Engineer · Multi-agent orchestration · LLM safety · Bedrock infra
          </p>
        </div>
      </section>

      <div className="section-divider mx-auto max-w-4xl" />

      {/* Intro */}
      <section ref={introRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-3xl space-y-6">
          <GlassCard accent className="reveal">
            <p className="text-foreground mb-4 text-xl font-medium">
              I build production AI systems — the kind that run unattended and still behave.
            </p>
            <p className="text-muted-foreground">
              My focus is the engineering that makes LLMs trustworthy in production — grounding gates, drift-proof
              context, and human-in-the-loop commits: gates that stop a model from shipping a claim it can’t support,
              context systems that keep an agent’s rules from drifting, and human-in-the-loop tooling that lets an agent
              operate a real system without being trusted to act unsupervised.
            </p>
          </GlassCard>
          <GlassCard className="reveal">
            <p className="text-muted-foreground">
              At Re:Build Manufacturing I build AI infrastructure — Terraform, observability, and RAG pipelines (a
              build-vs-buy call cut a team from six engineers to two; replacing a slow embedding path gave a 20x
              speedup). Generic application work is commoditizing fast, while agent and LLM-infrastructure work is where
              demand is heading. The systems below are how I stay on the right side of that shift.
            </p>
          </GlassCard>
          <p className="text-muted-foreground reveal text-center">
            Beyond software, I invest in real estate and direct a music festival —{' '}
            <a
              href="https://garrettjohnlaw.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary font-medium underline-offset-4 hover:underline"
            >
              garrettjohnlaw.com
            </a>{' '}
            ties it together.
          </p>
        </div>
      </section>

      <div className="section-divider mx-auto max-w-4xl" />

      {/* The systems */}
      <CaseStudies />

      <div className="section-divider mx-auto max-w-4xl" />

      {/* Principles */}
      <section ref={valuesRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-fluid-h2 text-foreground mb-10 text-center">How I build AI systems</h2>

          <div className="grid gap-4 md:grid-cols-2">
            {values.map((value) => (
              <GlassCard key={value.title} hover className="value-card">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                    <value.icon className="text-primary h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-foreground mb-1 font-semibold">{value.title}</h3>
                    <p className="text-muted-foreground text-sm">{value.description}</p>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider mx-auto max-w-4xl" />

      {/* CTA */}
      <section ref={ctaRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-2xl">
          <GlassCard accent className="reveal py-10 text-center">
            <Bot className="text-primary mx-auto mb-4 h-10 w-10" />
            <p className="text-muted-foreground mx-auto mb-6 max-w-lg">
              I’m comfortable architecting agent fleets, writing grounding gates, tuning Bedrock cost, and shipping it
              all to production. If you’re hiring for AI or agent engineering, I’d love to connect.
            </p>
            <Button asChild className="gradient-cyber text-primary-foreground hover:glow-cyber-sm">
              <Link to="/resume">
                View my résumé
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </GlassCard>
        </div>
      </section>
    </>
  )
}
